import { memo, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { cloneDeep, isNil, noop, random, values } from 'lodash';
// Import Types
import type { CJVisualizationDueDateUIModel, CJVisualizationStepOption, VisualizationIcon } from 'types';
// Import UI Components
import { ExtendedInput, Tabs, Tab, Button } from '@geneui/components';
// Import Components
import CustomerJourneyVisualizationDueDate from './CustomerJourneyVisualizationDueDate';
import CustomerJourneyVisualizationTemplateGroup from './CustomerJourneyVisualizationTemplateGroup';
import CustomerJourneyVisualizationSubStep from './CustomerJourneyVisualizationSubStep';
import CustomerJourneyVisualizationIconPicker from './CustomerJourneyVisualizationIconPicker';
import StepsSetting from 'components/StepsSetting';
// Import Helpers
import { itemsIsValid } from 'services/Helpers';
// Import Services
import {
    newVisualizationStepNameGenerator,
    getModalCollectedProperties,
    visualizationTemplatesHasContent,
    isCJVisualizationIconAllowed,
} from 'services/customerJourney/helpers';
// Import Constants
import { l, CJModelRelationScopes } from 'constants/common';
// Import Validations
import { yupSchemaValidate } from 'validators/yupUtils';
import { CJVisualizationStepIconValidationSchema } from 'validators/schemas.validation';
// Import Styles
import styles from './customerJourneyVisualizationStep.module.scss';

const minSubStepsCount = 1;

interface Props {
    defaultValue?: {
        templates?: AnyObject;
        key?: string;
        icon?: string;
        name?: string;
        description?: string;
        subSteps?: [];
        node?: Nullable<AnyObject>;
        dueDateOption?: Nullable<AnyObject>;
        dueDate?: Nilable<CJVisualizationDueDateUIModel>;
    };
    icons?: VisualizationIcon[];
    elements?: Nullable<AnyObject[]>;
    getUpdate?: (data: AnyObject) => void;
    stepNameGenerator: (name?: string) => string;
    generateSubStep?: (key: number, steps: AnyObject[]) => AnyObject;
    isSingleGroup?: boolean;
}

export interface CustomerJourneyVisualizationStepRef {
    changeLanguages: (languages: string[], partnerLanguages: AnyObject) => void;
}

const CustomerJourneyVisualizationStep = forwardRef<CustomerJourneyVisualizationStepRef, Props>((props, ref) => {
    const {
        elements = null,
        getUpdate = noop,
        defaultValue = {
            templates: {},
            name: '',
            description: '',
            key: '',
            subSteps: [],
            icon: '',
        },
        icons,
        generateSubStep,
        stepNameGenerator,
        isSingleGroup = false,
    } = props;

    const [tabActiveKey, setTabActiveKey] = useState('0');
    const [showSubStepSetting, setShowSubStepSetting] = useState(false);

    // updatable states
    const [iconValue, setIconValue] = useState<Nilable<string>>(defaultValue?.icon);
    const [templateData, setTemplateData] = useState(defaultValue?.templates);
    const [subStepsData, setSubStepsData] = useState<AnyObject[]>(defaultValue?.subSteps ?? []);
    const [dueDate, setDueDate] = useState<Nilable<CJVisualizationDueDateUIModel>>(defaultValue?.dueDate);

    const groupCollectedProperties = useMemo(
        () =>
            isNil(defaultValue?.node) ? [] : getModalCollectedProperties(CJModelRelationScopes.All, defaultValue.node),
        [defaultValue?.node],
    );

    // get update only blur
    const [name, setName] = useState(defaultValue?.name);
    const [description, setDescription] = useState(defaultValue?.description);

    useEffect(() => {
        getUpdate({
            name: name,
            description: description,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, description]);

    const { t } = useTranslation();

    const nameChangeHandler = (e) => {
        setName(e.target.value);
    };

    const descriptionChangeHandler = (e) => {
        setDescription(e.target.value);
    };

    const nameBlurHandler = () => {
        const tmpName = stepNameGenerator(name);
        setName(tmpName);
    };

    const descriptionBlurHandler = () => {
        setDescription(description);
    };

    const isTemplatesVisible = !isSingleGroup || visualizationTemplatesHasContent(templateData);

    useEffect(() => {
        const isDueDateValid = isNil(dueDate) || dueDate?.isValid === true;
        const isStepValid =
            (!isTemplatesVisible || itemsIsValid(values(templateData))) &&
            yupSchemaValidate(CJVisualizationStepIconValidationSchema(), { icon: iconValue }) &&
            isCJVisualizationIconAllowed(icons, iconValue) &&
            isDueDateValid &&
            itemsIsValid(subStepsData);
        getUpdate({
            isValid: isStepValid,
            icon: iconValue,
            templates: templateData,
            dueDate: dueDate,
            subSteps: subStepsData,
        });
        // DEPS: [getUpdate]
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateData, iconValue, icons, dueDate, subStepsData, isTemplatesVisible]);

    const getNewRenderKey = (item = { renderKey: -1 }) => {
        let tmpNewRenderKey = random(0, 10000);
        while (item?.renderKey === tmpNewRenderKey) {
            tmpNewRenderKey = random(0, 10000);
        }
        return tmpNewRenderKey;
    };

    const changeTemplateLanguages = (templatesValue, languages, partnerLanguages) => {
        return languages.reduce(
            (acc, language) => {
                if (isNil(templatesValue[language])) {
                    // a new language row starts with an empty (required) title, so it is invalid until filled
                    acc[language] = {
                        name: '',
                        description: '',
                        language: { flagId: language, label: partnerLanguages[language]?.Name },
                        isValid: false,
                    };
                } else {
                    acc[language] = templatesValue[language];
                }
                return acc;
            },
            { defaultLang: templatesValue.defaultLang },
        );
    };

    const hasInvalidTemplates = (templates: Nilable<AnyObject>): boolean => {
        return values(templates ?? {}).some((template) => template?.isValid === false);
    };

    const getSubStepValidityAfterLanguagesChange = (subStep: AnyObject): boolean => {
        const isSectionInvalid =
            (subStep.reward?.showReward === true && hasInvalidTemplates(subStep.reward.templates)) ||
            (subStep.button?.showButton === true && hasInvalidTemplates(subStep.button.templates)) ||
            (subStep.progress?.showProgress === true && hasInvalidTemplates(subStep.progress.templates));
        return hasInvalidTemplates(subStep.templates) || isSectionInvalid ? false : subStep.isValid;
    };

    useImperativeHandle(ref, () => ({
        changeLanguages(languages, partnerLanguages) {
            setTemplateData((prev) => {
                return changeTemplateLanguages(prev, languages, partnerLanguages);
            });
            setSubStepsData((prev) => {
                return prev.map((subStep) => {
                    const nextSubStep = {
                        ...subStep,
                        renderKey: getNewRenderKey(),
                        templates: changeTemplateLanguages(subStep.templates, languages, partnerLanguages),
                        ...(isNil(subStep.reward)
                            ? {}
                            : {
                                  reward: {
                                      ...subStep.reward,
                                      templates: changeTemplateLanguages(
                                          subStep.reward.templates ?? {},
                                          languages,
                                          partnerLanguages,
                                      ),
                                  },
                              }),
                        ...(isNil(subStep.button)
                            ? {}
                            : {
                                  button: {
                                      ...subStep.button,
                                      templates: changeTemplateLanguages(
                                          subStep.button.templates ?? {},
                                          languages,
                                          partnerLanguages,
                                      ),
                                  },
                              }),
                        ...(isNil(subStep.progress)
                            ? {}
                            : {
                                  progress: {
                                      ...subStep.progress,
                                      templates: changeTemplateLanguages(
                                          subStep.progress.templates ?? {},
                                          languages,
                                          partnerLanguages,
                                      ),
                                  },
                              }),
                    };
                    return { ...nextSubStep, isValid: getSubStepValidityAfterLanguagesChange(nextSubStep) };
                });
            });
        },
    }));

    const templateChangeHandler = (languageKey, data) => {
        setTemplateData((prev) => {
            return { ...prev, [languageKey]: { ...prev?.[languageKey], ...data } };
        });
    };

    const dueDateUpdateHandler = (dueDateValue: Partial<CJVisualizationDueDateUIModel>) => {
        setDueDate((prev) => ({ ...prev, ...dueDateValue }) as CJVisualizationDueDateUIModel);
    };

    const tabChangeHandler = (index) => {
        setTabActiveKey(`${index}`);
    };

    const addSubStepClickHandler = () => {
        setSubStepsData((prev) => {
            const result = cloneDeep(prev);
            // loaded steps are keyed by OrderKey (1..n), so the next key is max + 1 and not the length
            const nextKey = prev.reduce((max, subStep) => Math.max(max, Number(subStep.key) || 0), 0) + 1;
            result.push(generateSubStep?.(nextKey, cloneDeep(prev)) ?? {});
            return result;
        });
    };

    const subStepUpdateHandler = (index) => {
        return (stepData) => {
            setSubStepsData((prev) => {
                const result = [...prev];
                result[index] = { ...prev[index], ...stepData };
                return result;
            });
        };
    };

    const subStepNameGenerator = (subSteps, index) => {
        return (name) => {
            const steps = cloneDeep(subSteps);
            steps.splice(index, 1);
            return newVisualizationStepNameGenerator(t, name, steps, l.Step);
        };
    };

    const collectedPropertiesByNode = useRef(new WeakMap<AnyObject, AnyObject[]>());
    const getCollectedProperties = (oneLastBlock: Nilable<AnyObject>): AnyObject[] => {
        if (isNil(oneLastBlock)) {
            return [];
        }
        const cached = collectedPropertiesByNode.current.get(oneLastBlock);
        if (!isNil(cached)) {
            return cached;
        }
        const collectedProperties = getModalCollectedProperties(CJModelRelationScopes.All, oneLastBlock);
        collectedPropertiesByNode.current.set(oneLastBlock, collectedProperties);
        return collectedProperties;
    };

    const deleteSubStepHandler = (index) => {
        return () => {
            if (index === +tabActiveKey) {
                setTabActiveKey('0');
            } else if (index < +tabActiveKey) {
                setTabActiveKey(`${+tabActiveKey - 1}`);
            }
            setSubStepsData((prev) => {
                const result = cloneDeep(prev);
                result.splice(index, 1);
                result.forEach((item, i) => {
                    item.key = i;
                });
                return result;
            });
        };
    };

    const subStepSettingClickHandler = () => {
        setShowSubStepSetting(true);
    };

    const subStepSettingCloseHandler = () => {
        setShowSubStepSetting(false);
    };

    const subStepSettingOkHandler = (data) => {
        setSubStepsData((prev) => {
            // steps are matched by their client side uid (keys can repeat, uids never do)
            const tmpSubStepsData = cloneDeep(prev).reduce((acc, subStepData) => {
                acc[subStepData.uid ?? subStepData.key] = subStepData;
                return acc;
            }, {});

            return [
                ...data.map((item) => {
                    const tmpSubStep = tmpSubStepsData[item.id];
                    tmpSubStep.key = item.index;
                    return tmpSubStep;
                }),
            ];
        });
        setShowSubStepSetting(false);
    };

    const getSubStepsSettingsItems = (subStepsData) => {
        return subStepsData.map((item, index) => ({ index: index, name: item.name, id: item.uid ?? item.key }));
    };

    // "Pass Selected Step" unlock condition: only the other steps of this group can unlock a step.
    // The option lists are rebuilt only when a step uid or name changes, not on every steps state update
    const stepOptionsKey = subStepsData.map((subStep) => `${subStep.uid}:${subStep.name}`).join('|');
    const availableStepsByIndex = useMemo<CJVisualizationStepOption[][]>(
        () =>
            subStepsData.map((_, index) =>
                subStepsData
                    .filter((subStep, subStepIndex) => subStepIndex !== index && !isNil(subStep.uid))
                    .map((subStep) => ({ value: subStep.uid, label: subStep.name })),
            ),
        // DEPS: [subStepsData] (represented by stepOptionsKey)
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [stepOptionsKey],
    );

    return (
        <div className={classNames('crm-cj-visualization-step', styles.group)}>
            <div className="crm-cj-visualization-step-block">
                <div className="c-input-group-wrapper">
                    <ExtendedInput
                        appearance="outline"
                        inputSize="default"
                        labelAppearance="swap"
                        placeholder={t(l.Name)}
                        onChange={nameChangeHandler}
                        onBlur={nameBlurHandler}
                        value={isNil(name) ? '' : name}
                    />
                    <ExtendedInput
                        appearance="outline"
                        inputSize="default"
                        labelAppearance="swap"
                        placeholder={t(l.Description)}
                        onChange={descriptionChangeHandler}
                        onBlur={descriptionBlurHandler}
                        value={isNil(description) ? '' : description}
                    />
                </div>
                <CustomerJourneyVisualizationIconPicker icons={icons} value={iconValue} onChange={setIconValue} />
            </div>
            {isTemplatesVisible && (
                <CustomerJourneyVisualizationTemplateGroup templates={templateData} onChange={templateChangeHandler} />
            )}
            {!isNil(dueDate) && (
                <CustomerJourneyVisualizationDueDate
                    collectedProperties={groupCollectedProperties}
                    node={defaultValue?.node}
                    elements={elements}
                    option={defaultValue?.dueDateOption}
                    getUpdate={dueDateUpdateHandler}
                    defaultValue={dueDate}
                />
            )}
            <Tabs
                className={styles.groupSteps}
                type="box"
                activeKey={tabActiveKey}
                contentPadding={20}
                actions={
                    <div className={styles.groupStepsActions}>
                        <Button appearance="minimal" icon="bc-icon-add" onClick={addSubStepClickHandler}>
                            {t(l.AddStep)}
                        </Button>
                        <Button icon="bc-icon-settings" appearance="minimal" onClick={subStepSettingClickHandler} />
                    </div>
                }
                onChange={tabChangeHandler}
            >
                {subStepsData.map((subStep, index) => {
                    return (
                        <Tab
                            key={index}
                            className={classNames({
                                active: +tabActiveKey === index,
                                [styles.groupStepInvalid]: !subStep.isValid,
                            })}
                            title={subStep.name}
                            onClose={subStepsData.length > minSubStepsCount ? deleteSubStepHandler(index) : undefined}
                        >
                            <CustomerJourneyVisualizationSubStep
                                collectedProperties={getCollectedProperties(subStep.node)}
                                node={subStep.node}
                                elements={elements}
                                option={subStep.option}
                                rewardOption={subStep.rewardOption}
                                rewardClientBonusIdOption={subStep.rewardClientBonusIdOption}
                                unlockOption={subStep.unlockOption}
                                dueDateOption={subStep.dueDateOption}
                                progressOption={subStep.progressOption}
                                availableSteps={availableStepsByIndex[index] ?? []}
                                icons={icons}
                                key={`CustomerJourneyVisualizationSubStep_${subStep.renderKey}`}
                                defaultValue={subStep}
                                getUpdate={subStepUpdateHandler(index)}
                                stepNameGenerator={subStepNameGenerator(subStepsData, index)}
                            />
                        </Tab>
                    );
                })}
            </Tabs>
            {showSubStepSetting && (
                <StepsSetting
                    isVisible={showSubStepSetting}
                    onCancel={subStepSettingCloseHandler}
                    onOk={subStepSettingOkHandler}
                    steps={getSubStepsSettingsItems(subStepsData)}
                    columnName={l.Step}
                />
            )}
        </div>
    );
});

CustomerJourneyVisualizationStep.displayName = 'CustomerJourneyVisualizationStep';

export default memo(CustomerJourneyVisualizationStep);
